import React from "react";
import PropTypes from "prop-types";
import { Select, 
    Checkbox, 
    Popconfirm, message, Button
} from "antd";
import { Table } from "components";
import { OrderedMap, List } from "immutable";
import _noop from "lodash/noop";
import _lower from "lodash/toLower";
import _debounce from "lodash/debounce";
import uuid from "uuid/v4";
import moment from "moment";
import UnderwritingService from "services/underwriting-service";
import { ellipsisText } from "components/ellipsis-text";
import { LEDGER_DATA_SOURCE } from "constants/enums";

const base64ToArrayBuffer = data => {
    var input = data.substring(data.indexOf(",") + 1);
    var binaryString = window.atob(input);
    var binaryLen = binaryString.length;
    var bytes = new Uint8Array(binaryLen);
    for (var i = 0; i < binaryLen; i++) {
        var ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
    }
    return bytes;
};

const downloadFileLocally = (res, fileType, type = "application/vnd.ms-excel") => {
    var blob = new Blob([res], { type });
    var elem = window.document.createElement("a");
    elem.href = window.URL.createObjectURL(blob);
    elem.download = `ledger.${fileType}`;
    document.body.appendChild(elem);
    elem.click();
    document.body.removeChild(elem);
};

const downloadPaymentStatus = (disabled, supplychain_id) => {
    const download = () => {
        message.loading("File Download in progress, may take few seconds... ", 0);
        UnderwritingService.getLedgerFile(supplychain_id, true, true).then(_ => {
            message.destroy();
            if(_.error){
                console.error(_.error);
            } else {
                if(_.data && _.data.ok) {
                    const data = base64ToArrayBuffer(_.data && _.data.ok);
                    downloadFileLocally(data, "xlsx");
                } else if(_.data && _.data.zip) {
                    const data = base64ToArrayBuffer(_.data && _.data.zip);
                    downloadFileLocally(data, "zip");
                }
            }
        }).catch(() => {
            message.destroy();
            message.error("Failed to Download File");
        });
    };
    return (
        <Button disabled={disabled} onClick={download} type={"link"} size={"small"}>
            {"Download"}
        </Button>
    );
};

const downloadFile = (disabled, supplychain_id) => {
    return (
        <Popconfirm
            title={"Which type of file you want to download ?"}
            okText={"Excel"}
            cancelText={"Pdf"}
            onConfirm={() => {
                message.loading("File Download in progress, may take few seconds... ", 0);
                UnderwritingService.getLedgerFile(supplychain_id, true).then(_ => {
                    message.destroy();
                    if(_.data && _.data.ok) {
                        const data = base64ToArrayBuffer(_.data && _.data.ok);
                        downloadFileLocally(data, "xlsx");
                    } else if(_.data && _.data.zip) {
                        const data = base64ToArrayBuffer(_.data && _.data.zip);
                        downloadFileLocally(data, "zip");
                    }
                }).catch(() => {
                    message.destroy();
                    message.error("Failed to Download File");
                });
            }}
            onCancel={() => {
                message.loading("File Download in progress, may take few seconds... ", 0);
                // PDF — raw arraybuffer, file type off the content-type header
                // (same as the Credit Appraisal ledger "Download").
                UnderwritingService.getLedgerFile(supplychain_id, false, false, "pdf").then(_ => {
                    message.destroy();
                    const contentType = _.headers && _.headers["content-type"];
                    if(_.data && contentType === "application/pdf") {
                        downloadFileLocally(_.data, "pdf", "application/pdf");
                    } else if(_.data && contentType === "application/zip") {
                        downloadFileLocally(_.data, "zip", "application/zip");
                    }
                }).catch(() => {
                    message.destroy();
                    message.error("Failed to Download File");
                });
            }}
        >
            <Button disabled={disabled} type={"link"} size={"small"}>
                {"Download"}
            </Button>
        </Popconfirm>
    );
};

const Option = Select.Option;

const SUPPLY_CHAIN_COLS = new OrderedMap({
    ledger: {
        title: "Ledger",
        dataIndex: "name",
        key: "ledger",
        width: 250,
        align: "center"
    },
    seller: {
        title: "Seller",
        dataIndex: "seller",
        key: "seller",
        width: 300,
        render: _ => _ && ellipsisText(`${_.name}(${_.gstin})`),
        align: "center",
    },
    anchor: {
        title: "Anchor",
        dataIndex: "anchor",
        key: "anchor",
        width: 300,
        render: _ => _ && ellipsisText(`${_.name}(${_.gstin})`),
        align: "center",
    },
    date_of_analyses: {
        title: "Analysed On",
        dataIndex: "create_date",
        key: "create_date",
        render: _ => moment(_).format("DD MMM YY"),
        width: 150,
        align: "center"
    },
    last_sync_date: {
        title: "Last Sync Date",
        dataIndex: "last_sync_date",
        key: "last_sync_date",
        render: _ => _ ? moment(_).format("DD MMM YY") : " -- ",
        width: 150,
        align: "center"
    },
    data_source_type: {
        title: "Data Source Type",
        dataIndex: "data_source_type",
        key: "data_source_type",
        width: 200,
        align: "center",
        render: _ => LEDGER_DATA_SOURCE.findKey(__ => __ == _)
    },
    date_of_appointment: {
        title: "Date of Appointment With Anchor",
        dataIndex: "date_of_appointment",
        key: "date_of_appointment",
        render: _ => _ && moment(_).format("DD MMM YY"),
        width: 200,
        align: "center"
    },
    avg_repayment_period: {
        title: "Avg Repayment Period",
        dataIndex: "avg_repayment_period",
        key: "avg_repayment_period",
        width: 200,
        align: "center",
        render: _ => _ || "N/A"
    },
    future_dated_debit_amount: {
        title: "Future Dated Debit Amount",
        dataIndex: "future_dated_debit_amount",
        key: "future_dated_debit_amount",
        width: 200,
        align: "center"
    },
    future_dated_credit_amount: {
        title: "Future Dated Credit Amount",
        dataIndex: "future_dated_credit_amount",
        key: "future_dated_credit_amount",
        width: 200,
        align: "center"
    },
    net_sales_amount_last_12_months: {
        title: "Net Sales(Last 12 months)",
        dataIndex: "ledger_analysis.net_sales_amount_last_12_months",
        key: "net_sales_amount_last_12_months",
        width: 200,
        align: "center"
    },
    // tenor: {
    //     title: "Tenor",
    //     dataIndex: "tenor",
    //     key: "tenor",
    //     width: 100,
    //     align: "center"
    // },
    proposed_limit: {
        title: "Limit",
        dataIndex: "ledger_analysis.limit_basis_sales",
        key: "proposed_limit",
        width: 100,
        align: "center"
    },
    score: {
        title: "Score",
        dataIndex: "score",
        key: "score",
        render: (_, record) => record.score_obtained ? `${record.score_obtained}/${record.max_score}` : "--",
        width: 100,
        align: "center"
    },
    system_recommendation: {
        title: "System Recommendation",
        dataIndex: "system_recommendation",
        key: "system_recommendation",
        width: 200,
        align: "center"
    },
    remarks: {
        title: "Remarks",
        dataIndex: "ledger_analysis.remarks",
        key: "remarks",
        width: 200,
        align: "center"
    },
    download_file: {
        title: "Download Ledger",
        dataIndex: "download_file",
        key: "download_file",
        width: 100,
        align: "center"
    },
    // download_payment_status: {
    //     title: "Payment Status",
    //     dataIndex: "download_payment_status",
    //     key: "download_payment_status",
    //     width: 100,
    //     align: "center"
    // }
});

class MultiTableSelect extends React.Component {
    state = {
        loading: false,
        searchString: null,
        dataSource: [],
        values: [],
        valuesAdded: false
    }

    componentDidMount() {
        this.setState({
            values: this.props.value.map((_, idx) => {
                return {
                    ..._,
                    tempId: (_.ledger_analysis && _.ledger_analysis.id) + " | " + (_.supplychain_id),
                    index: idx
                };
            }),
            valuesAdded: true
        });
    }

    componentDidUpdate(nextProps) {
        if(this.props.value && this.props.value.length && (nextProps.value && nextProps.value.length !== this.props.value.length)) {
            // let distinctObjs = [...new Set(this.props.value.map(_ => _.id))];
            // this.props.value.filter(_ => distinctObjs.includes(_.id))
            this.setState({
                values: this.props.value.map(_ => {
                    return {
                        ..._,
                        tempId: (_.ledger_analysis && _.ledger_analysis.id) + " | " + (_.supplychain_id)
                    };
                }),
                valuesAdded: true
            });
        }
    }

    parentContainerClassName = "object-select-parent-" + uuid();
    debouncedSearch = _debounce(str => {
        this.props.onSearch(str).then(({ data }) => {
            if (data) {
                this.setState({
                    dataSource: data
                });
                this.forceUpdate();
            }
        });
    }, this.props.debounce||2000);
    
    onSearch = str =>
        this.props.showSearch ? this.debouncedSearch(str) : null;

    onSelect = value => {
        const { valueFunc } = this.props;
        const valueObj = this.state.dataSource.find(_ => value === valueFunc(_));
        this.props.onChange([...this.props.value, {...valueObj, checked: true }]);
    }

    onToggleSelect = recordId => {
        this.props.onChange(this.state.values.map(_ => {
            if(_.tempId === recordId) {
                if(_.checked) {
                    _.checked = false;
                } else {
                    _.checked = true;
                }
                return _;
            }
            return _;
        }));
    }
    onToggleSelectAll = (recordId, bool) => {
        this.props.onChange(this.state.values.map(_ => {
            if(_.tempId === recordId) {
                bool ?
                    _.checked = true 
                    : 
                    _.checked = false;
                return _;
            }
            return _;
        }));
    }

    onSelectAll = (checked) => {
        this.state.values && this.state.values.map((el)=>{ 
            this.onToggleSelectAll(el && el.tempId, checked);
        });
        
    }

    tableCols = new OrderedMap({
        "checked": {
            title: <p><Checkbox className={"px-5"}  defaultChecked={true} onChange={(e) => {
                return(
                    this.onSelectAll(e.target.checked));}}/></p>,
            dataIndex: "checked",
            key: "checked",
            width: 50,
            render: (_, record) => <Checkbox className={"px-5"} checked={_} onClick={() => this.onToggleSelect(record.tempId)}/>
        }
    }).merge(SUPPLY_CHAIN_COLS);

    render() {
        const {
            placeholder,
            disabled,
            showSearch,
            showArrow,
            loading,
            className,
            // value,
            style,
            valueFunc,
            labelFunc,
            keyFunc
        } = this.props;      

        return(
            <div>
                <div className={this.parentContainerClassName}>
                    <Select
                        autoClearSearchValue={false}
                        className={className}
                        placeholder={placeholder}
                        disabled={disabled}
                        mode={"default"}
                        dropdownMenuStyle={this.props.dropdownMenuStyle}
                        filterOption={(inputValue, option) => {
                            if(_lower(option.props.children).includes(_lower(inputValue))) {
                                return true;
                            }
                            return false;
                        }}
                        onSearch={this.onSearch || _noop}
                        onSelect={this.onSelect || _noop}
                        onFocus={this.onFocus || _noop}
                        showSearch={showSearch || false}
                        showArrow={showArrow || false}
                        style={style || {minWidth: 200}}
                        loading={loading || this.loading || false}
                        getPopupContainer={() => document.getElementsByClassName(this.parentContainerClassName)[0]}
                        defaultOpen={this.props.open}
                    >
                        {this.state.dataSource.map((_) => (
                            <Option
                                key={keyFunc(_)}
                                value={valueFunc(_)}
                            >
                                {labelFunc(_)}
                            </Option>
                        ))}
                    </Select>
                </div>
                <Table
                    data={new List(this.state.values && this.state.values.map(_ => {
                        let row = _;
                        if(!row.ledger_analysis || (row.ledger_analysis && !row.ledger_analysis.id)) {
                            row.download_file = downloadFile.bind(null, true,  _.supplychain_id)();
                            row.download_payment_status = downloadPaymentStatus.bind(null, true, _.supplychain_id)();
                            return row;
                        }
                        row.download_file = downloadFile.bind(null, false,  _.supplychain_id)();
                        row.download_payment_status = downloadPaymentStatus.bind(null, false, _.supplychain_id)();
                        return row;
                    }))}
                    columns={this.tableCols}
                    checkedColumns={[]}
                    keyFilter={"tempId"}
                    showTopSection={false}
                    size={"small"}
                    type={"inner"}
                    noRowSelection={true}
                />
            </div>
        );
    }   
}


MultiTableSelect.defaultProps = {
    onSearch: _noop,
    onSelect: _noop,
    onDeselect: _noop,
    onChange: _noop,
    debounce: 400,
    labelProp: "text",
    value: [],
    tableData: [],
    getOptionsOnFocus: () => Promise.resolve({}),
};

MultiTableSelect.propTypes = {
    open: PropTypes.bool,
    placeholder: PropTypes.string,
    mode: PropTypes.string,
    onSearch: PropTypes.func,
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    onDeselect: PropTypes.func,
    disabled: PropTypes.bool,
    showSearch: PropTypes.bool,
    showArrow: PropTypes.bool,
    loading: PropTypes.bool,
    debounce: PropTypes.number,
    labelProp: PropTypes.string,
    className: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    tableData: PropTypes.array,
    getOptionsOnFocus: PropTypes.func,
    dropdownMenuStyle: PropTypes.object,
    onFocus: PropTypes.func,
    onCommit: PropTypes.func,
    style: PropTypes.object,
    popupContainer: PropTypes.object,
    valueFunc: PropTypes.func,
    labelFunc: PropTypes.func,
    keyFunc: PropTypes.func
};


export default MultiTableSelect;